import {
  Button,
  Icon,
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useDuplicateUserGeneratedContentMutation,
  duplicateErrorMessageKey,
} from '../../../../common/queries/use-user-generated-content-query';
import { usePushToastOrFlashMessage } from '../../../v2/toaster/deprecation-helper';
import { FeedCardActionsProps } from './type';

export function FeedCardActionDuplicate({
  content,
  isHidden,
}: FeedCardActionsProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { pushMessage } = usePushToastOrFlashMessage();

  const { isWorking, mutate } = useDuplicateUserGeneratedContentMutation(
    content.id,
    {
      onSuccess: (newId: number) => {
        history.push(`/post/${newId}`);
      },
      onError: (error) => {
        pushMessage({
          type: 'error',
          text: t(duplicateErrorMessageKey(error)),
        });
      },
    }
  );

  const label = t('content_submission.duplicate', 'Duplicate');
  const disabledTooltip = t(
    'content_submission.duplicate_incompatible',
    "This post contains blocks that are incompatible with the editor and can't be duplicated."
  );

  const isDuplicatable = !!content.is_duplicatable;
  const isDisabled = !isDuplicatable || isWorking;

  const handleClick = () => {
    if (isDisabled) return;
    mutate();
  };

  if (isHidden) {
    return (
      <TooltipRoot>
        <TooltipContent
          description={!isDuplicatable ? disabledTooltip : label}
        />
        <TooltipTrigger asChild>
          <Button
            variant="text"
            size="compact"
            label={label}
            leftIcon={<Icon>content_copy</Icon>}
            onClick={handleClick}
            disabled={isDisabled}
          />
        </TooltipTrigger>
      </TooltipRoot>
    );
  }

  return (
    <TooltipRoot>
      <TooltipContent description={!isDuplicatable ? disabledTooltip : label} />
      <TooltipTrigger asChild>
        <IconButton
          aria-label={label}
          iconName="content_copy"
          size="compact"
          onClick={handleClick}
          disabled={isDisabled}
        />
      </TooltipTrigger>
    </TooltipRoot>
  );
}
